import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, startAfter, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';
import { useResultsPool } from './useResultsPool';
import { useModalPerformanceMonitoring } from './usePerformanceMonitoring';

/**
 * Jobs Data Management Hook
 * 
 * Provides comprehensive jobs data management without order dependencies:
 * - Direct job queries by user
 * - Pagination and filtering
 * - Real-time subscriptions
 * - Results pooling
 * - Performance monitoring
 */

export const useJobsData = ({
  filters = {},
  pageSize = 20,
  modalId = 'jobs-only-data',
  enableRealTime = true,
  userScope = 'current_user' // 'all' | 'current_user' | 'other_users'
} = {}) => {
  console.log('🔄 useJobsData initialized with filters:', filters);
  const { userId, isLoading: authLoading } = useAuth();
  const { trackAsyncOperation } = useModalPerformanceMonitoring(modalId, true);

  // State management
  const [state, setState] = useState({
    jobs: [],
    loading: false,
    error: null,
    hasMore: true,
    lastDoc: null,
    totalCount: 0,
    lastUpdated: null
  });

  const lastDocRef = useRef(null);
  const isLoadingRef = useRef(false);
  const unsubscribeRef = useRef(null);

  // Extract filter values
  const {
    casefileIds = [],
    evidenceIds = [],
    statuses = [],
    providers = [],
    dateRange
  } = filters;

  // Debug: Log filters being used
  useEffect(() => {
    console.log('🔍 useJobsData filters:', {
      casefileIds,
      evidenceIds,
      statuses,
      providers,
      dateRange,
      userScope
    });
  }, [casefileIds, evidenceIds, statuses, providers, dateRange, userScope]);

  // Memoized filter key for cache invalidation
  const filterKey = useMemo(() => 
    JSON.stringify({
      casefileIds: casefileIds.sort(),
      evidenceIds: evidenceIds.sort(),
      statuses: statuses.sort(),
      providers: providers.sort(),
      dateRange: dateRange ? {
        start: dateRange.start?.toISOString(),
        end: dateRange.end?.toISOString()
      } : null
    }),
    [casefileIds, evidenceIds, statuses, providers, dateRange]
  );

  // Get current job IDs for results pooling
  const currentJobIds = useMemo(() => 
    state.jobs.map(job => job.id).filter(Boolean),
    [state.jobs]
  );

  // Use results pool for efficient results loading
  const { results: pooledResults } = useResultsPool(currentJobIds);

  // Create network fetcher function
  const createNetworkFetcher = useCallback((reset = false) => {
    return async () => {
      const currentFilters = filtersRef.current;
      console.log('🔄 createNetworkFetcher: Building query with filters:', currentFilters);
      let qRef = collection(db, 'jobs');
      const constraints = [];
      
      // Apply user scope filtering
      if (userScope === 'current_user') {
        constraints.push(where('userId', '==', userIdRef.current));
      } else if (userScope === 'other_users') {
        constraints.push(where('userId', '!=', userIdRef.current));
      }
      // For 'all' scope, no user filtering is applied
      
      // Apply evidence filtering
      if (currentFilters.evidenceIds.length > 0) {
        if (currentFilters.evidenceIds.length === 1) {
          constraints.push(where('evidenceId', '==', currentFilters.evidenceIds[0]));
        } else {
          // For multiple evidence IDs, we'll filter client-side
        }
      }
      
      // Apply casefile filtering
      if (currentFilters.casefileIds.length > 0) {
        if (currentFilters.casefileIds.length === 1) {
          constraints.push(where('casefileId', '==', currentFilters.casefileIds[0]));
        } else {
          constraints.push(where('casefileId', 'in', currentFilters.casefileIds.slice(0, 10)));
        }
      }
      
      // Apply status filtering
      if (currentFilters.statuses.length > 0) {
        constraints.push(where('status', 'in', currentFilters.statuses));
      }
      
      // Apply provider filtering
      if (currentFilters.providers.length > 0) {
        constraints.push(where('provider', 'in', currentFilters.providers));
      }
      
      // Add ordering and pagination
      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(pageSize));
      
      if (!reset && lastDocRef.current) {
        constraints.push(startAfter(lastDocRef.current));
      }
      
      qRef = query(qRef, ...constraints);
      console.log('🔄 createNetworkFetcher: Executing query with constraints:', constraints.length);
      const snap = await getDocs(qRef);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('🔄 createNetworkFetcher: Found', items.length, 'jobs');
      
      // Update pagination reference
      if (snap.docs.length > 0) {
        lastDocRef.current = snap.docs[snap.docs.length - 1];
      }
      
      return items;
    };
  }, [pageSize, userScope]); // Added userScope dependency

  // Client-side filtering for complex queries
  const applyClientSideFilters = useCallback((items) => {
    let filtered = items;
    const currentFilters = filtersRef.current;
    
    // Multiple evidence IDs filtering (client-side)
    if (currentFilters.evidenceIds.length > 1) {
      filtered = filtered.filter(item => 
        currentFilters.evidenceIds.includes(item.evidenceId)
      );
    }
    
    // Date range filtering
    if (currentFilters.dateRange) {
      filtered = filtered.filter(item => {
        const itemDate = item.createdAt?.toDate?.() || new Date(item.createdAt);
        return (!currentFilters.dateRange.start || itemDate >= currentFilters.dateRange.start) &&
               (!currentFilters.dateRange.end || itemDate <= currentFilters.dateRange.end);
      });
    }
    
    return filtered;
  }, []); // No dependencies since using refs

  // Store latest values in refs to avoid dependency issues
  const userIdRef = useRef(userId);
  const authLoadingRef = useRef(authLoading);
  const filtersRef = useRef({ evidenceIds, casefileIds, statuses, providers, dateRange });
  
  // Update refs when values change
  useEffect(() => {
    userIdRef.current = userId;
    authLoadingRef.current = authLoading;
    filtersRef.current = { evidenceIds, casefileIds, statuses, providers, dateRange };
  }, [userId, authLoading, evidenceIds, casefileIds, statuses, providers, dateRange]);

  // Load jobs data
  const loadJobs = useCallback(async (reset = false) => {
    console.log('🔄 loadJobs called', { userId: userIdRef.current, authLoading: authLoadingRef.current, isLoading: isLoadingRef.current, reset, userScope });
    // For 'all' scope, we don't need userId. For other scopes, we need it.
    const needsUserId = userScope !== 'all';
    if ((needsUserId && !userIdRef.current) || authLoadingRef.current || isLoadingRef.current) return;
    
    if (reset) {
      lastDocRef.current = null;
    }
    
    isLoadingRef.current = true;
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null,
      ...(reset && { jobs: [], hasMore: true })
    }));

    try {
      const loadOperation = async () => {
        const fetcher = createNetworkFetcher(reset);
        const items = await fetcher();
        const filtered = applyClientSideFilters(items);
        
        return {
          items: filtered,
          hasMore: items.length === pageSize,
          reset
        };
      };

      const { items, hasMore } = await trackAsyncOperation('loadJobs', loadOperation());

      setState(prev => ({
        ...prev,
        jobs: reset ? items : [...prev.jobs, ...items],
        hasMore,
        loading: false,
        totalCount: reset ? items.length : prev.totalCount + items.length,
        lastUpdated: Date.now()
      }));

      console.log(`📊 Jobs loaded: ${items.length} jobs (reset: ${reset})`);

    } catch (error) {
      console.error('Error loading jobs:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    } finally {
      isLoadingRef.current = false;
    }
  }, [createNetworkFetcher, applyClientSideFilters, trackAsyncOperation, pageSize, userScope]); // userScope needed for needsUserId check

  // Real-time subscription
  const subscribeToJobs = useCallback(() => {
    // For 'all' scope, we don't need userId. For other scopes, we need it.
    const needsUserId = userScope !== 'all';
    if ((needsUserId && !userIdRef.current) || authLoadingRef.current || !enableRealTime) return;

    // Unsubscribe from previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    try {
      // Create query for real-time jobs
      let qRef = collection(db, 'jobs');
      const currentFilters = filtersRef.current;
      const constraints = [
        orderBy('createdAt', 'desc'),
        limit(pageSize * 2) // Get more for real-time updates
      ];
      
      // Apply user scope filtering for real-time
      if (userScope === 'current_user') {
        constraints.unshift(where('userId', '==', userIdRef.current));
      } else if (userScope === 'other_users') {
        constraints.unshift(where('userId', '!=', userIdRef.current));
      }
      // For 'all' scope, no user filtering is applied

      // Apply basic filters to real-time query
      if (currentFilters.statuses.length > 0) {
        constraints.push(where('status', 'in', currentFilters.statuses));
      }
      
      if (currentFilters.providers.length > 0) {
        constraints.push(where('provider', 'in', currentFilters.providers));
      }

      qRef = query(qRef, ...constraints);

      // Subscribe to real-time updates
      const unsubscribe = onSnapshot(qRef, 
        (snapshot) => {
          const jobs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          const filtered = applyClientSideFilters(jobs);

          setState(prev => ({
            ...prev,
            jobs: filtered,
            lastUpdated: Date.now(),
            hasMore: jobs.length === pageSize * 2 // Estimate
          }));

          console.log(`🔄 Jobs real-time update: ${filtered.length} jobs`);
        },
        (error) => {
          console.error('Error in jobs real-time subscription:', error);
          setState(prev => ({
            ...prev,
            error: error.message
          }));
        }
      );

      unsubscribeRef.current = unsubscribe;

    } catch (error) {
      console.error('Error setting up jobs real-time subscription:', error);
      setState(prev => ({
        ...prev,
        error: error.message
      }));
    }
  }, [enableRealTime, applyClientSideFilters, pageSize, userScope]); // Added userScope dependency

  // Load initial data when filters change
  // Note: loadJobs and subscribeToJobs are intentionally NOT in dependencies to prevent infinite loops
  const prevFilterKeyRef = useRef(filterKey);
  const hasLoadedForFilterRef = useRef(false);
  
  useEffect(() => {
    const filterChanged = prevFilterKeyRef.current !== filterKey;
    
    if (filterChanged) {
      console.log('🔄 useJobsData: Filter changed, resetting load flag');
      hasLoadedForFilterRef.current = false;
      prevFilterKeyRef.current = filterKey;
    }
    
    // Skip if we've already loaded for this filter (prevents StrictMode duplicate loads)
    if (hasLoadedForFilterRef.current) {
      console.log('🔄 useJobsData: Skipping load - already loaded for this filter');
      return;
    }
    
    console.log('🔄 useJobsData: Effect triggered', { userId, authLoading, filterKey });
    if (userId && !authLoading) {
      console.log('🔄 useJobsData: Loading jobs...');
      loadJobs(true); // Reset and load
      hasLoadedForFilterRef.current = true;
      
      if (enableRealTime) {
        subscribeToJobs();
      }
    }
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, userId, authLoading, enableRealTime, userScope]); // loadJobs and subscribeToJobs intentionally excluded

  // Load more jobs
  const loadMore = useCallback(() => {
    if (!state.hasMore || state.loading) return;
    loadJobs(false);
  }, [state.hasMore, state.loading, loadJobs]);

  // Refresh jobs
  const refresh = useCallback(() => {
    loadJobs(true);
    if (enableRealTime) {
      subscribeToJobs();
    }
  }, [loadJobs, enableRealTime, subscribeToJobs]);

  // Get job by ID
  const getJobById = useCallback((jobId) => {
    return state.jobs.find(job => job.id === jobId);
  }, [state.jobs]);

  // Statistics
  const stats = useMemo(() => {
    const jobs = state.jobs;
    
    return {
      total: jobs.length,
      byStatus: {
        pending: jobs.filter(j => j.status === 'pending').length,
        processing: jobs.filter(j => j.status === 'processing').length,
        completed: jobs.filter(j => j.status === 'completed').length,
        failed: jobs.filter(j => j.status === 'failed').length
      },
      byProvider: jobs.reduce((acc, job) => {
        const provider = job.provider || 'unknown';
        acc[provider] = (acc[provider] || 0) + 1;
        return acc;
      }, {}),
      withResults: Object.keys(pooledResults).length,
      totalCost: jobs.reduce((sum, job) => sum + (job.actualCost || 0), 0),
      avgCost: jobs.length > 0 ? 
        jobs.reduce((sum, job) => sum + (job.actualCost || 0), 0) / jobs.length : 0
    };
  }, [state.jobs, pooledResults]);

  // Enhanced job data with results
  const jobsWithResults = useMemo(() => {
    return state.jobs.map(job => ({
      ...job,
      results: pooledResults[job.id] || null,
      hasResults: !!pooledResults[job.id]
    }));
  }, [state.jobs, pooledResults]);

  return {
    // Data
    jobs: state.jobs,
    jobsWithResults,
    results: pooledResults,
    stats,
    
    // State
    loading: state.loading,
    error: state.error,
    hasMore: state.hasMore,
    totalCount: state.totalCount,
    lastUpdated: state.lastUpdated,
    isSubscribed: !!unsubscribeRef.current,
    
    // Actions
    loadMore,
    refresh,
    
    // Utilities
    getJobById,
    
    // Metadata
    isReady: !authLoading && !!userId
  };
};
